import { GQL_ID } from '@@partner:types:graphql'
import React, { useMemo } from 'react'
import { Drawer, Button, ButtonToolbar, Panel, List, Tag, Loader, Message } from 'rsuite'
import { Link, useParams, generatePath } from 'react-router-dom'
import { WorkShift } from '@@partner:types:app'
import { useFetchOrders } from '@@partner:graphql/query/order/allOrders.gql'
import { makeFilter, makeBetweenFilter } from '@@partner:helpers/crud/filters'
import { ROUTE_ORDERS_VIEW_ORDER } from '../../../config/routes'
import { formatOrderStatusLabel } from '@@partner:helpers/formatters/order'
import { ORDER } from '@hive/status'
import { formatWorkshiftStatusLabel } from '@@partner:helpers/formatters/workshift'
import { formatPrice } from '@@partner:helpers/util'


export interface ShiftDetailDrawerProps {
  isOpen: boolean
  shift?: WorkShift | null
  onClose: () => void
  onEdit?: (shift: WorkShift) => void
}

export default function ShiftDetailDrawer ({
  isOpen,
  shift,
  onClose,
  onEdit
}: ShiftDetailDrawerProps) {
  const { brandSlug } = useParams<{ brandSlug: string }>()

  // Функции для форматирования данных
  const formatDateTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return {
      date: date.toLocaleDateString('ru-RU'),
      time: date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    }
  }

  // Функция для генерации пути к заказу
  const getOrderPath = (orderId: GQL_ID) => {
    if (!brandSlug) return '#'
    
    return generatePath(ROUTE_ORDERS_VIEW_ORDER, {
      brandSlug,
      orderId
    })
  }

  // Создаем фильтр для заказов этой смены
  const ordersFilter = useMemo(() => {
    if (!shift?.assembler?.id) return null
    
    return {
      and: [
        // Фильтр по курьеру
        makeFilter('assembler_id', 'eq', [ shift.assembler.id ]),
        // Фильтр по времени смены - заказы должны попадать в период смены
        makeBetweenFilter('slotStartingAt', {
          from: Number(shift.plannedStartAt),
          to: Number(shift.plannedFinishAt)
        })
      ]
    }
  }, [ shift ])

  // Запрос заказов для этой смены
  const { data: ordersData, loading: ordersLoading, error: ordersError } = useFetchOrders({
    filter: ordersFilter || undefined,
    pager: { page: 1, limit: 100 },
    sort: {
      direction: 0,
      fieldName: 'updated_at'
    }
  })

  const orders = useMemo(() => ordersData?.orders?.edges || [], [ ordersData ])

  // Статистика заказов
  const orderStats = useMemo(() => {
    const total = orders.length
    const assembled = orders.filter(order => [ ORDER.WAITING_FOR_COURIER, ORDER.DELIVERY_IN_PROGRESS, ORDER.DELIVERED, ORDER.REFUNDED, ORDER.WAITING_FOR_PICKUP ].includes(order.status)).length
    const delivered = orders.filter(order => order.status === ORDER.DELIVERED).length // статус "доставлен"
    const cancelled = orders.filter(order => order.status === ORDER.CANCELED).length // статус "отменен"
    const totalRevenue = orders
      .filter(order => order.status === ORDER.DELIVERED)
      .reduce((sum, order) => sum + (order.assembledOrderTotal || 0), 0)
    
    return {
      total,
      assembled,
      delivered,
      cancelled,
      inProgress: total - assembled - cancelled,
      deliveryRate: total > 0 ? Math.round((assembled / total) * 100) : 0,
      totalRevenue
    }
  }, [ orders ])


  if (!shift) return null

  const startTime = formatDateTime(Number(shift.plannedStartAt))
  const endTime = formatDateTime(Number(shift.plannedFinishAt))
  const statusInfo = formatWorkshiftStatusLabel(shift.status)
  
  const actualStartTime = shift.startedAt ? formatDateTime(Number(shift.startedAt)) : null
  const actualEndTime = shift.finishedAt ? formatDateTime(Number(shift.finishedAt)) : null

  return (
    <Drawer size="lg" placement="right" open={isOpen} onClose={onClose}>
      <Drawer.Header>
        <Drawer.Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>📋 Детали смены</span>
            <Tag color={statusInfo.color as any} size="sm">
              {statusInfo.label}
            </Tag>
          </div>
        </Drawer.Title>
      </Drawer.Header>

      <Drawer.Body style={{ 
        padding: '20px',
        overflowX: 'hidden',
        maxWidth: '100%'
      }}>
        {/* Основная информация о смене */}
        <Panel header="👤 Основная информация" bordered collapsible defaultExpanded style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                  Сборщик
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {shift.assembler ? `${shift.assembler.first_name} ${shift.assembler.last_name}` : 'Не назначен'}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                  Дата смены
                </div>
                <div style={{ fontSize: '14px' }}>
                  {startTime.date}
                </div>
              </div>
            </div>
            
            <div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
                  ID смены
                </div>
                <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#666' }}>
                  #{shift.id}
                </div>
              </div>
            </div>
          </div>
        </Panel>

        {/* Время смены */}
        <Panel header="⏰ Время смены" bordered collapsible defaultExpanded style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#1976d2' }}>
                📅 Запланировано
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Начало</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {startTime.time}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Окончание</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {endTime.time}
                </div>
              </div>
            </div>
            
            <div>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#388e3c' }}>
                ⚡ Фактически
              </div>
              {actualStartTime ? (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Начато</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#388e3c' }}>
                    {actualStartTime.time}
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Начато</div>
                  <div style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
                    Не начата
                  </div>
                </div>
              )}
              
              {actualEndTime ? (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Завершено</div>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#388e3c' }}>
                    {actualEndTime.time}
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Завершено</div>
                  <div style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
                    Не завершена
                  </div>
                </div>
              )}
            </div>
          </div>
        </Panel>

        {/* Магазины курьера */}
        {shift.assembler?.shop && (
          <Panel 
            header={'🏪 Магазин'} 
            bordered 
            collapsible 
            defaultExpanded 
            style={{ marginBottom: '20px' }}
          >
            <List size="sm" hover>
              <List.Item key={shift.assembler.shop.id}>
                <div style={{ padding: '8px 0' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                    {shift.assembler.shop.name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    📍 {shift.assembler.shop.address}
                  </div>
                </div>
              </List.Item>
            </List>
          </Panel>
        )}

        {/* Заказы - реальные данные */}
        <Panel 
          header={`📦 Заказы ${orderStats.total > 0 ? `(${orderStats.total})` : ''}`} 
          bordered 
          collapsible 
          defaultExpanded 
          style={{ marginBottom: '20px' }}
        >
          {/* Статистика заказов */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
            gap: '12px',
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#1976d2' }}>{orderStats.total}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Всего заказов</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#388e3c' }}>{orderStats.assembled}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Собрано</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#ff9800' }}>{orderStats.inProgress}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>В работе</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#d32f2f' }}>{orderStats.cancelled}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Отменено</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '20px', fontWeight: '600', color: '#1976d2' }}>{orderStats.deliveryRate}%</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Успешность</div>
            </div>
            <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#fff', borderRadius: '6px' }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#388e3c' }}>{ formatPrice(orderStats.totalRevenue) }</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Выручка</div>
            </div>
          </div>

          {/* Список заказов */}
          {ordersLoading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Loader content="Загрузка заказов..." />
            </div>
          )}

          {ordersError && (
            <Message type="error" style={{ marginBottom: '16px' }}>
              Ошибка загрузки заказов: {ordersError.message}
            </Message>
          )}

          {!ordersLoading && !ordersError && orders.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              color: '#666'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>📭</div>
              <div>Заказов для этой смены не найдено</div>
            </div>
          )}

          {!ordersLoading && orders.length > 0 && (
            <List 
              size="sm" 
              hover 
              style={{ 
                maxWidth: '100%',
                overflow: 'hidden'
              }}
            >
              {orders.map((order, index) => {
                const orderStatus = formatOrderStatusLabel(order.status)
                const orderTime = order.deliveryStartedAt ? 
                  formatDateTime(Number(order.deliveryStartedAt)) : 
                  null
                
                return (
                  <List.Item key={order.id} index={index}>
                    <Link 
                      to={getOrderPath(order.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        textDecoration: 'none',
                        color: 'inherit',
                        display: 'block',
                        borderRadius: '6px',
                        margin: '2px 0',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa'
                        e.currentTarget.style.transform = 'translateX(4px)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.transform = 'translateX(0)'
                      }}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '8px 12px',
                        transition: 'background-color 0.2s',
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          flex: 1, 
                          minWidth: 0, 
                          marginRight: '12px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <span style={{ 
                              fontWeight: '600', 
                              fontFamily: 'monospace',
                              fontSize: '13px',
                              color: '#1976d2'
                            }}>
                              #{order.id}
                            </span>
                            <Tag color={orderStatus.color as any} size="sm">
                              {orderStatus.label}
                            </Tag>
                          </div>
                          
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            📍 {order.address || 'Адрес не указан'}
                          </div>
                          
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            👤 {order.customer?.fullName || order.customer_phone}
                          </div>
                        </div>
                        
                        <div style={{ 
                          textAlign: 'right',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          gap: '2px',
                          minWidth: '100px',
                          flexShrink: 0
                        }}>
                          <div style={{ 
                            fontWeight: '600', 
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            { formatPrice(order.total) }
                            <span style={{ fontSize: '10px', color: '#1976d2' }}>↗</span>
                          </div>
                          {orderTime && (
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {orderTime.time}
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  </List.Item>
                )
              })}
            </List>
          )}
        </Panel>
      </Drawer.Body>

      <div style={{ 
        padding: '16px 20px', 
        borderTop: '1px solid #e5e5e5',
        backgroundColor: '#fff',
        position: 'sticky',
        bottom: 0
      }}>
        <ButtonToolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button appearance="subtle" onClick={onClose}>
            Закрыть
          </Button>
          {onEdit && (
            <Button 
              appearance="primary" 
              onClick={() => onEdit(shift)}
            >
              Редактировать смену
            </Button>
          )}
        </ButtonToolbar>
      </div>
    </Drawer>
  )
}